/* iter-quota-pill — "12/20 sends left today" pill for the user inbox.
 *
 * Pulls the `quota` block from `/api/inbox/my-alias`. Colour bands:
 *   green   — >30% of the daily cap remaining
 *   amber   — 10-30% remaining
 *   rose    — <10% remaining OR suspended
 * Trial users get a "reply-only" chip instead of a numeric pill.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, AlertTriangle, Ban } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function QuotaPill() {
  const [q, setQ] = useState(null);
  const [alias, setAlias] = useState('');

  useEffect(() => {
    let stop = false;
    const load = () => axios.get(`${API}/inbox/my-alias`, { withCredentials: true })
      .then((r) => { if (stop) return; setQ(r.data?.quota); setAlias(r.data?.alias || ''); })
      .catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  if (!q) return null;

  if (q.suspended) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/60 bg-rose-50 px-2 py-1 text-[10.5px] font-medium text-rose-800"
            title={`Alias ${alias} suspended — contact support to appeal`}
            data-testid="quota-pill-suspended">
        <Ban className="h-3 w-3" /> Suspended
      </span>
    );
  }

  const remaining = Math.max(0, (q.sends_last_day_limit || 0) - (q.sends_last_day || 0));
  const pct = q.sends_last_day_limit ? remaining / q.sends_last_day_limit : 1;
  const tone = pct < 0.1 ? 'rose' : pct < 0.3 ? 'amber' : 'emerald';
  const cls = tone === 'rose'
    ? 'border-rose-500/60 bg-rose-50 text-rose-800'
    : tone === 'amber'
    ? 'border-amber-500/60 bg-amber-50 text-amber-900'
    : 'border-emerald-500/60 bg-emerald-50 text-emerald-800';
  const Icon = tone === 'rose' ? AlertTriangle : Mail;

  const trialSuffix = q.tier === 'trial' ? ' · reply-only' : '';
  const title = `${alias} · ${q.tier} tier · ${q.sends_last_day}/${q.sends_last_day_limit} sends used today · ${q.sends_last_hour}/${q.sends_last_hour_limit} this hour${trialSuffix}`;

  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10.5px] font-medium ${cls}`}
          title={title}
          data-testid="quota-pill">
      <Icon className="h-3 w-3" />
      <span className="font-mono-stat">{remaining}/{q.sends_last_day_limit}</span>
      <span className="text-[9.5px] uppercase tracking-wider opacity-70">
        left today{trialSuffix ? ' · reply-only' : ''}
      </span>
    </span>
  );
}
